<template>
  <q-dialog v-model="shown">
    <q-card
      v-if="!$_.isNil(modelId)"
      style="max-width: 100vw; width: 700px;"
    >
      <q-inner-loading
        style="z-index: 1000"
        :showing="loading"
      >
        <q-spinner size="xl" />
      </q-inner-loading>
      <q-card-section class="row q-pb-none">
        <q-icon
          name="save_alt"
          size="md"
          class="q-mr-sm"
        />
        <div class="text-h6">
          Archive {{ modelClass }} ({{ modelId }})
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>

      <q-tabs
        v-model="tab"
        active-color="primary"
        indicator-color="primary"
        content-class="text-grey-8"
        align="left"
        no-caps
      >
        <q-tab
          name="blocks"
          :disable="blocksCount === 0"
        >
          <div class="text-h7">
            Blocking Issues ({{ blocksCount }})
          </div>
        </q-tab>
        <q-tab
          name="changes"
        >
          <div class="text-h7">
            Pending Changes ({{ changesCount }})
          </div>
        </q-tab>
      </q-tabs>
      <q-tab-panels
        v-model="tab"
        animated
      >
        <q-tab-panel
          name="blocks"
          class="scroll"
          style="max-height: 80vh;"
        >
          <div class="callout-box row q-gutter-x-sm items-center justify-center text-body1 q-mx-sm">
            <q-icon
              class="q-mr-sm"
              name="far fa-triangle-exclamation"
              size="sm"
            />
            To archive this {{ modelClass }}, you'll need to resolve the following issues
          </div>
          <q-card-section class="column q-pb-none">
            <q-expansion-item
              v-model="expandBlocks"
              class="q-mb-lg q-mt-sm q-px-sm q-py-xs border-open"
              header-class="q-px-none"
              :disable="directBlocksCount === 0"
            >
              <template #header>
                <div class="row full-width q-gutter-x-xs items-center">
                  <div class="text-body-em">
                    Blocking Items ({{ directBlocksCount }})
                  </div>
                  <div class="text-body1">
                    The following need to be reconfigured to archive this {{ modelClass }}
                  </div>
                </div>
              </template>
              <template #default>
                <archive-blocks-section
                  v-if="directBlocksCount > 0"
                  class="q-ml-md"
                  :model-id="modelId"
                  :label="modelClass"
                  :blocks="directBlocks"
                  hide-header
                  :hide-view="hideView"
                />
              </template>
            </q-expansion-item>
            <q-expansion-item
              v-model="expandArchives"
              class="q-my-sm q-px-sm q-py-xs border-open"
              header-class="q-px-none"
              :disable="$_.isEmpty(subBlocks)"
            >
              <template #header>
                <div class="row full-width q-gutter-x-xs items-center">
                  <div class="text-body-em">
                    Archives ({{ archivesBlocksCount }})
                  </div>
                  <div class="text-body1">
                    The following must also be unblocked to archive with this {{ modelClass }}
                  </div>
                </div>
              </template>
              <template #default>
                <div
                  v-for="subBlock in subBlocks"
                  :key="`sub-block-${subBlock.modelName}-${subBlock.id}`"
                  class="q-ml-sm"
                >
                  <archive-blocks-section
                    :model-name="subBlock.modelName"
                    :model-id="subBlock.id"
                    :label="subBlock.label"
                    :blocks="subBlock.blocks"
                    :hide-view="hideView"
                  />
                </div>
              </template>
            </q-expansion-item>
          </q-card-section>
        </q-tab-panel>
        <q-tab-panel
          name="changes"
          class="scroll"
          style="max-height: 80vh;"
        >
          <div class="callout-box row q-gutter-x-sm items-center justify-center text-body1 q-mx-sm">
            <q-icon
              class="q-mr-sm"
              name="far fa-circle-exclamation"
              size="sm"
            />
            When this {{ modelClass }} is archived, the following changes will be applied
          </div>
          <q-card-section class="column q-pb-none">
            <archive-confirmation-section
              :warns="warns"
              :nullifies="nullifies"
              bordered
            />
            <q-expansion-item
              v-model="expandArchives"
              class="q-mt-lg q-mb-sm q-px-sm q-py-xs border-open"
              header-class="q-px-none"
              :disable="$_.isEmpty(archives)"
            >
              <template #header>
                <div class="row full-width q-gutter-x-xs items-center">
                  <div class="text-body-em">
                    Archives ({{ archivesBlocksCount }})
                  </div>
                  <div class="text-body1">
                    The following will also be archived with this {{ modelClass }}
                  </div>
                </div>
              </template>
              <template #default>
                <div
                  class="column q-gutter-y-xs q-ml-sm"
                >
                  <template
                    v-for="association in archives"
                    :key="`archives-${association.name}`"
                  >
                    <q-expansion-item
                      v-for="change in association.changes"
                      :key="`${association.name}-${change.id}`"
                      v-model="expandArchiveAssociationModels[association.name][change.id]"
                      dense
                      header-class="small-expansion-toggle text-body1 q-px-none"
                      switch-toggle-side
                    >
                      <template #header>
                        <div
                          class="row full-width q-gutter-x-xs items-center"
                          style="font-size: 0.9rem;"
                        >
                          <q-icon
                            class="q-mr-xs"
                            name="far fa-box-archive"
                            color="primary"
                            size="xs"
                          />
                          <div
                            class="text-body1"
                          >
                            {{ change.label }}
                          </div>
                          <div
                            class="text-body1 text-grey-8"
                          >
                            (ID {{ change.id }})
                          </div>
                        </div>
                      </template>
                      <template #default>
                        <archive-confirmation-section
                          class="q-ml-md"
                          :warns="change.changes.warns"
                          :nullifies="change.changes.nullifies"
                          :hide-view="hideView"
                        />
                      </template>
                    </q-expansion-item>
                  </template>
                </div>
              </template>
            </q-expansion-item>
          </q-card-section>
        </q-tab-panel>
      </q-tab-panels>
      <q-card-actions
        class="q-mx-sm q-pt-none q-pb-lg q-px-lg"
        align="right"
      >
        <q-btn
          class="text-bold"
          style="max-width: 40%; width: 150px; border-radius: 8px; font-size: 1rem;"
          label="Archive"
          color="white"
          text-color="black"
          dense
          no-caps
          :unelevated="false"
          :disable="blocksCount !== 0"
          @click="archive(false)"
        >
          <q-tooltip v-if="blocksCount !== 0">
            Must resolve all blocking issues to archive.
          </q-tooltip>
        </q-btn>
        <q-btn
          class="text-bold"
          style="max-width: 40%; width: 150px; border-radius: 8px; font-size: 1rem;"
          label="Archive & Remove"
          color="primary"
          :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
          dense
          no-caps
          :unelevated="false"
          :disable="blocksCount !== 0"
          @click="archive(true)"
        >
          <q-tooltip v-if="blocksCount !== 0">
            Must resolve all blocking issues to archive.
          </q-tooltip>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import ArchiveBlocksSection from 'src/modules/admin/components/archiving/ArchiveBlocksSection'
import ArchiveConfirmationSection from 'src/modules/admin/components/archiving/ArchiveConfirmationSection'

export default {
  name: 'ArchiveConfirmationDialog',
  components: {
    ArchiveBlocksSection,
    ArchiveConfirmationSection
  },
  configuration: ['currentSite'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    hideView: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ['update:model-value', 'before-archive', 'after-archive'],
  models: {
    archivableModel: {
      variables () {
        return {
          modelClass: this.modelClass,
          modelId: this.modelId,
          skip: _.isNil(this.modelClass) || _.isNil(this.modelId)
        }
      }
    }
  },
  data () {
    return {
      shown: false,
      tab: 'blocks',
      model: null,
      modelClass: null,
      modelId: null,
      archives: [],
      directBlocks: [],
      subBlocks: [],
      warns: [],
      nullifies: [],
      expandArchives: true,
      expandBlocks: true,
      expandArchiveAssociationModels: {},
      loading: false
    }
  },
  computed: {
    directBlocksCount () {
      return _.values(this.directBlocks).length
    },
    archivesBlocksCount () {
      return _(this.subBlocks).map(subModel => _(subModel.blocks).values().flatten().value().length).sum()
    },
    blocksCount () {
      return this.directBlocksCount + this.archivesBlocksCount
    },
    directChangesCount () {
      return _(this.warns).map('changes').flatten().value().length + _(this.nullifies).map('changes').flatten().value().length
    },
    archivesChangesCounts () {
      const archivesChangesCounts = {}
      _.each(this.archives, association => {
        archivesChangesCounts[association.name] = {}
        _.each(association.changes, change => {
          archivesChangesCounts[association.name][change.id] = _(change.changes.nullifies).map('changes').flatten().value().length + _(change.changes.warns).map('changes').flatten().value().length
        })
      })
      return archivesChangesCounts
    },
    archivesChangesCount () {
      return _(this.archivesChangesCounts).map(subModel => _(subModel).values().flatten().value().length).sum()
    },
    changesCount () {
      return this.directChangesCount + this.archivesChangesCount
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    }
  },
  methods: {
    async show (modelClass, modelId) {
      try {
        this.modelClass = modelClass
        this.modelId = modelId

        this.loading = true
        this.shown = true

        await this.$nextTick()
        await this.archivableModel.fetch(true)

        const warns = _.cloneDeep(this.archivableModel.warns)
        const nullifies = _.cloneDeep(this.archivableModel.nullifies)

        this.warns = warns
        this.nullifies = nullifies

        const blocks = _.cloneDeep(this.archivableModel.blocks)
        this.directBlocks = blocks

        const archives = _.cloneDeep(this.archivableModel.archives)
        _.each(archives, association => {
          const associationName = association.name
          _.each(association.changes, changes => {
            this.expandArchiveAssociationModels[associationName] = {}
            _.each(changes, change => {
              this.expandArchiveAssociationModels[associationName][change.id] = false
            })
          })
        })
        this.archives = archives
        this.subBlocks = _(archives).map(association => {
          return _(association.changes).map(change => {
            if (_.keys(change.changes.blocks).length === 0) {
              return null
            }

            return {
              associationName: association.label,
              id: change.id,
              label: change.label,
              blocks: change.changes.blocks
            }
          }).compact().value()
        }).flatten().value()

        if (_.isEmpty(this.directBlocks) && _.isEmpty(this.subBlocks)) {
          this.tab = 'changes'
        } else {
          this.tab = 'blocks'
        }
      } catch (error) {
        this.hide()
      } finally {
        this.loading = false
      }
    },
    hide () {
      this.shown = false
      this.reset()
    },
    reset () {
      this.modelClass = null
      this.modelId = null
      this.tab = 'blocks'
      this.archives = []
      this.directBlocks = []
      this.subBlocks = []
      this.warns = []
      this.nullifies = []
      this.expandArchives = true
      this.expandArchiveAssociations = {}
      this.expandArchiveAssociationModels = {}
    },
    async archive (nullify = false) {
      try {
        this.loading = true
        this.$emit('before-archive', this.modelId)
        const results = await this.archiveModel(this.modelClass, this.modelId, nullify)
        if (!_.isEmpty(results?.errors)) {
          this.$mh.showErrors(results.errors)
          await this.archivableModel.fetch(true)
        } else {
          this.hide()
          this.$emit('after-archive', this.modelId)
        }
      } finally {
        this.loading = false
      }
    },
    async archiveModel (modelClass = this.modelClass, modelId = this.modelId, nullify = false) {
      return await this.archivableModel.archiveModel(modelClass, modelId, nullify)
    },
    async unarchiveModel (modelClass = this.modelClass, modelId = this.modelId) {
      return await this.archivableModel.unarchiveModel(modelClass, modelId)
    }
  }
}

</script>

<style lang="scss" scoped>
.callout-box {
  border-radius: 8px !important;
  background-color: color-mix(in lab, transparent 30%, var(--q-warning));
  backdrop-filter: blur(4px);
  font-size: 1.1rem;
  padding: 8px;
}

.q-expansion-item :deep(.small-expansion-toggle >.q-item__section--avatar) {
  padding-right: 0;
  min-width: 0;
}

.border-open {
  border: 1px solid #D5D5D5;
  border-radius: 6px;
}
</style>
